import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Form from '../Form';
import Text from '../Text';
import { Button } from '../Button';
import { Column, Row } from '../Layout/Grid';
import { Attachment } from '../Attachment';
import { OpenSlideSuccess } from '../OpenSlideSuccess';

import { ContactPresidentProps, FileTypesAccept, ContactDefault } from './interface';
import { PhoneNumberOutput } from 'utils/PhoneNumber';
import { RootState } from 'store/ducks';

import UserServices from 'services/user.service';
import { validateFile } from '../../utils/validateFile';

import {
  InputNameEnterprise,
  TextAreaMessage,
  InputName,
  InputPhone,
  InputEmail,
  InputSubject
} from './styles';

export const ContactPresident: React.FC = () => {
  const dispatch = useDispatch();

  const MAX_FILE_UPLOAD = 3;
  const MAX_FILE_SIZE_UPLOAD = 15000000; //15MB

  const user = useSelector((state: RootState) => state.user);
  const customer = useSelector((state: RootState) => state.customer);

  const _userService = new UserServices();

  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [files, setFiles] = useState<Array<File>>([]);
  const [data, setData] = useState<ContactPresidentProps>(ContactDefault);

  useEffect(() => {
    if (user?.selectedCustomerId || customer?.id) {
      setData({
        ...data,
        customerId: customer?.id ?? user?.selectedCustomerId,
      });
    }
  }, [user, customer]);

  const addFile = ({ target }: any) => {
    const filesArray: Array<File> = [...target.files];
    const fileTypes: Array<string> = FileTypesAccept;

    validateFile(filesArray, setErrorMessage, files,
      MAX_FILE_UPLOAD, MAX_FILE_SIZE_UPLOAD) &&
      filesArray && filesArray.forEach((file) => {
        fileTypes.includes(file.type) && setFiles((f) => [...f, file]);
      });

    target.value = "";
  };

  const onSubmitContact = async () => {
    setLoading(true);
    setErrorMessage("");

    data.phone = PhoneNumberOutput(data.phone);
    const response = await _userService.sendContactPresident(files, data);

    if (response.status === 200) {
      dispatch({ type: 'HIDE_TITLE', });
      setSuccess(true);
    } else {
      setErrorMessage(response.data.data);
    }

    setLoading(false);
  };

  return (
    success ? (
      <OpenSlideSuccess
        title='Agradecemos o contato'
        description='Sua mensagem foi enviada com sucesso.'
        titleBold={true}
        titleFontSize={18}
      />
    ) : (
      <Form
        className="flex-1"
        id="send_message_president_form"
        onValidate={() => onSubmitContact()}
      >
        <Column className="w-100 max-w-lg-100 flex-1 px-0 mt-4">
          <Row className="flex-md-column">
            <Column className="flex-1 ml-sm-0 mt-md-2">
              <Text className="color-blue-gray mb-2">Nome*</Text>
              <InputName
                value={data.name}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setData({ ...data, name: target.value })
                }
                disabled={loading}
              />
            </Column>
            <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
              <Text className="color-blue-gray mb-2">E-mail*</Text>
              <InputEmail
                value={data.email}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setData({ ...data, email: target.value })
                }
                disabled={loading}
              />
            </Column>
          </Row>

          <Row className="flex-md-column mt-4 mt-md-0">
            <Column className="flex-1 ml-sm-0 mt-md-2">
              <Text className="color-blue-gray mb-2">Empresa*</Text>
              <InputNameEnterprise
                value={data.enterpriseName}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setData({ ...data, enterpriseName: target.value })
                }
                disabled={loading}
              />
            </Column>
            <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
              <Text className="color-blue-gray mb-2">Telefone*</Text>
              <InputPhone
                value={PhoneNumberOutput(data.phone)}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setData({ ...data, phone: PhoneNumberOutput(target.value) })
                }
                disabled={loading}
              />
            </Column>
          </Row>

          <Row className="flex-md-column mt-4 mt-md-0">
            <Column className="flex-1 ml-sm-0 mt-md-2">
              <Text className="color-blue-gray mb-2">Assunto*</Text>
              <InputSubject
                value={data.subject}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setData({ ...data, subject: target.value })
                }
                disabled={loading}
              />
            </Column>
          </Row>

          <Row className="flex-md-column mt-4 mt-md-0">
            <Column className="flex-1 ml-sm-0 mt-md-2 relative">
              <Text className="color-blue-gray mb-2">Mensagem*</Text>
              <TextAreaMessage
                value={data.message}
                onChange={({ target }) =>
                  setData({ ...data, message: target.value })
                }
                disabled={loading}
              />
            </Column>
          </Row>

          <Column className="mt-4">
            <Attachment
              addFile={addFile}
              setFiles={setFiles}
              files={files}
              fileTypesAccept={FileTypesAccept}
            />
          </Column>

          {errorMessage && (
            <Text size={14} className="color-danger animated fadeIn mt-3">
              {errorMessage}
            </Text>
          )}

          <Row className="mt-5 align-center justify-center">
            <Button
              form="send_message_president_form"
              spinnerColor="#0E4C54"
              isRequesting={loading}
              disabled={loading || !data?.message}
              className="outline bg-primary flex-sm-1 align-center justify-center d-flex"
            >
              <Text className="color-primary font-bold px-2">Enviar Contato</Text>
            </Button>
          </Row>
        </Column>
      </Form>
    )
  );
};